/**
 * Scorecards — rubrics + evaluation results.
 *
 * Top: list of scorecards (grouped by blueprint), with rule count and compute button.
 * Bottom: per-blueprint grid of entities × scorecards with level badges.
 */
import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Award, RefreshCw, Plus, Eye, AlertCircle, Play, Shield, Boxes, Circle,
  CheckCircle2, XCircle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Rule {
  identifier: string
  title: string
  level: string
  query: { combinator: string; conditions: any[] }
}

interface Scorecard {
  identifier: string
  title: string
  blueprint_identifier: string
  rules: Rule[]
  created_at: string
}

interface Blueprint {
  identifier: string
  title: string
  schema: any[]
}

interface GridCell {
  scorecard_identifier: string
  scorecard_title: string
  level: string
  score: number
}

interface GridRow {
  entity_identifier: string
  entity_title: string
  cells: GridCell[]
}

interface Grid {
  blueprint: string
  scorecards: { identifier: string; title: string }[]
  rows: GridRow[]
}

const LEVEL_COLOR: Record<string, string> = {
  gold: 'bg-amber text-graphite',
  silver: 'bg-whitesmoke/70 text-graphite',
  bronze: 'bg-orange-400 text-graphite',
  none: 'bg-gunmetal text-whitesmoke/50',
}

const LEVEL_BORDER: Record<string, string> = {
  gold: 'border-amber/50',
  silver: 'border-whitesmoke/30',
  bronze: 'border-orange-500/40',
  none: 'border-gunmetal/50',
}

export default function ScorecardsPage() {
  const [scorecards, setScorecards] = useState<Scorecard[]>([])
  const [blueprints, setBlueprints] = useState<Blueprint[]>([])
  const [selectedBp, setSelectedBp] = useState<string>('')
  const [grid, setGrid] = useState<Grid | null>(null)
  const [drillIn, setDrillIn] = useState<{ sc: string; ent: string } | null>(null)
  const [drillData, setDrillData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [computing, setComputing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [scRes, bpRes] = await Promise.all([
        api.get('/api/scorecards'),
        api.get('/api/blueprints'),
      ])
      setScorecards(scRes.data.scorecards || [])
      setBlueprints(bpRes.data.blueprints || [])
      if (!selectedBp && scRes.data.scorecards?.length) {
        setSelectedBp(scRes.data.scorecards[0].blueprint_identifier)
      }
      setError(null)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Load failed')
    } finally {
      setLoading(false)
    }
  }, [selectedBp])

  const loadGrid = useCallback(async (bp: string) => {
    if (!bp) return
    try {
      const { data } = await api.get(`/api/scorecards/grid/${bp}`)
      setGrid(data)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Grid load failed')
    }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => { if (selectedBp) loadGrid(selectedBp) }, [selectedBp, loadGrid])

  const computeAll = async () => {
    setComputing(true)
    try {
      await api.post('/api/scorecards/compute-all')
      if (selectedBp) await loadGrid(selectedBp)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Compute failed')
    } finally {
      setComputing(false)
    }
  }

  const computeOne = async (ident: string) => {
    setComputing(true)
    try {
      await api.post(`/api/scorecards/${ident}/compute`)
      if (selectedBp) await loadGrid(selectedBp)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Compute failed')
    } finally {
      setComputing(false)
    }
  }

  const openDrill = async (sc: string, ent: string) => {
    setDrillIn({ sc, ent })
    try {
      const { data } = await api.get(`/api/scorecards/${sc}/results`)
      const row = (data.results || []).find((r: any) => r.entity_identifier === ent)
      setDrillData(row)
    } catch {
      setDrillData(null)
    }
  }

  // Group scorecards by blueprint
  const byBlueprint: Record<string, Scorecard[]> = {}
  for (const sc of scorecards) {
    if (!byBlueprint[sc.blueprint_identifier]) byBlueprint[sc.blueprint_identifier] = []
    byBlueprint[sc.blueprint_identifier].push(sc)
  }

  const bpsWithScorecards = Object.keys(byBlueprint)

  // Summary stats
  const stats = grid ? (() => {
    const all = grid.rows.flatMap(r => r.cells)
    return {
      total: all.length,
      gold: all.filter(c => c.level === 'gold').length,
      silver: all.filter(c => c.level === 'silver').length,
      bronze: all.filter(c => c.level === 'bronze').length,
      none: all.filter(c => c.level === 'none').length,
      avg: all.length ? Math.round(all.reduce((s, c) => s + c.score, 0) / all.length) : 0,
    }
  })() : null

  return (
    <div className="min-h-screen bg-graphite text-whitesmoke">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gunmetal">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-3">
              <Award className="w-6 h-6 text-amber" />
              Scorecards
            </h1>
            <p className="text-sm text-whitesmoke/60 mt-1">
              Rubrics of rules evaluated against entities. Bronze → Silver → Gold levels.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={computeAll}
              disabled={computing}
              className="px-3 py-1.5 rounded bg-amber/20 hover:bg-amber/30 text-amber text-sm flex items-center gap-1.5"
            >
              <RefreshCw className={clsx('w-4 h-4', computing && 'animate-spin')} />
              Compute all
            </button>
            <Link
              to="/app/blueprints"
              className="px-3 py-1.5 rounded bg-gunmetal hover:bg-gunmetal/80 text-sm flex items-center gap-1.5"
            >
              <Boxes className="w-4 h-4" />
              Blueprints
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-rose/10 border border-rose/30 text-rose text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Scorecard list */}
        <div className="mb-6 border border-gunmetal rounded overflow-hidden">
          <div className="bg-gunmetal/40 px-3 py-2 text-xs uppercase tracking-wider text-whitesmoke/60">
            Scorecards ({scorecards.length}) — grouped by blueprint
          </div>
          {scorecards.length === 0 && (
            <div className="p-6 text-center text-sm text-whitesmoke/50">
              No scorecards yet. Visit Blueprints and click <strong>Seed system blueprints</strong>.
            </div>
          )}
          {bpsWithScorecards.map(bpId => {
            const bp = blueprints.find(b => b.identifier === bpId)
            return (
              <div key={bpId} className="border-t border-gunmetal/50">
                <button
                  onClick={() => setSelectedBp(bpId)}
                  className={clsx(
                    'w-full text-left px-3 py-2 flex items-center justify-between hover:bg-gunmetal/30',
                    selectedBp === bpId && 'bg-sky/10',
                  )}
                >
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Boxes className="w-4 h-4 text-whitesmoke/60" />
                    {bp?.title || bpId}
                    <span className="text-xs text-whitesmoke/40 font-normal">({byBlueprint[bpId].length})</span>
                  </span>
                </button>
                <ul>
                  {byBlueprint[bpId].map(sc => (
                    <li key={sc.identifier} className="px-3 py-2 border-t border-gunmetal/30 flex items-center justify-between text-sm">
                      <div>
                        <div className="font-medium">{sc.title}</div>
                        <div className="text-xs text-whitesmoke/50 mt-0.5">
                          {sc.rules.length} rule{sc.rules.length === 1 ? '' : 's'} —
                          {' '}{sc.rules.filter(r => r.level === 'bronze').length} bronze,
                          {' '}{sc.rules.filter(r => r.level === 'silver').length} silver,
                          {' '}{sc.rules.filter(r => r.level === 'gold').length} gold
                        </div>
                      </div>
                      <button
                        onClick={() => computeOne(sc.identifier)}
                        className="text-xs px-2 py-1 rounded bg-amber/10 hover:bg-amber/20 text-amber flex items-center gap-1"
                      >
                        <Play className="w-3 h-3" /> compute
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>

        {/* Grid */}
        {selectedBp && grid && (
          <>
            {stats && (
              <div className="grid grid-cols-5 gap-2 mb-4">
                <Tile label="Total cells" value={stats.total} />
                <Tile label="Gold" value={stats.gold} color="text-amber" />
                <Tile label="Silver" value={stats.silver} color="text-whitesmoke" />
                <Tile label="Bronze" value={stats.bronze} color="text-orange-400" />
                <Tile label="Avg score" value={`${stats.avg}`} color={stats.avg >= 80 ? 'text-emerald' : stats.avg >= 60 ? 'text-amber' : 'text-rose'} />
              </div>
            )}
            <div className="border border-gunmetal rounded overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gunmetal/40 text-xs uppercase tracking-wider text-whitesmoke/60 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-normal sticky left-0 bg-gunmetal/95 z-10">Entity</th>
                    {grid.scorecards.map(sc => (
                      <th key={sc.identifier} className="px-3 py-2 font-normal text-left min-w-[140px]">{sc.title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grid.rows.map(row => (
                    <tr key={row.entity_identifier} className="border-t border-gunmetal/50">
                      <td className="px-3 py-2 sticky left-0 bg-graphite font-medium">
                        <Link to={`/app/entities/${selectedBp}`} className="hover:text-sky">
                          {row.entity_title}
                        </Link>
                        <div className="text-xs text-whitesmoke/50 font-mono">{row.entity_identifier}</div>
                      </td>
                      {row.cells.map(c => (
                        <td key={c.scorecard_identifier} className="px-3 py-2">
                          <button
                            onClick={() => openDrill(c.scorecard_identifier, row.entity_identifier)}
                            className={clsx(
                              'inline-flex items-center gap-2 px-2 py-1 rounded border text-xs',
                              LEVEL_BORDER[c.level],
                            )}
                            title="Click to see rule breakdown"
                          >
                            <span className={clsx('inline-flex w-2 h-2 rounded-full', LEVEL_COLOR[c.level])}></span>
                            <span className="font-mono">{c.score}</span>
                            <span className="text-whitesmoke/60 uppercase text-[10px]">{c.level}</span>
                          </button>
                        </td>
                      ))}
                    </tr>
                  ))}
                  {grid.rows.length === 0 && (
                    <tr>
                      <td colSpan={grid.scorecards.length + 1} className="py-8 text-center text-whitesmoke/40">
                        No entities yet for this blueprint.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {drillIn && drillData && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setDrillIn(null) }}
        >
          <div className="bg-graphite border border-gunmetal rounded max-w-xl w-full">
            <div className="bg-gunmetal/40 px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm text-whitesmoke/60">Scorecard breakdown</div>
                <div className="text-lg font-semibold">{drillIn.ent} · {drillIn.sc}</div>
              </div>
              <button onClick={() => setDrillIn(null)} className="text-whitesmoke/60 hover:text-whitesmoke">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-3 mb-4">
                <span className={clsx('px-3 py-1 rounded font-bold text-lg', LEVEL_COLOR[drillData.level])}>
                  {drillData.level.toUpperCase()}
                </span>
                <span className="font-mono text-2xl">{drillData.score}/100</span>
              </div>
              <ul className="space-y-1.5">
                {(drillData.breakdown || []).map((b: any, i: number) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    {b.passed ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald" />
                    ) : (
                      <Circle className="w-4 h-4 text-rose" />
                    )}
                    <span className="flex-1">{b.rule_title}</span>
                    <span className="text-xs uppercase text-whitesmoke/50">{b.level}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Tile({ label, value, color = 'text-whitesmoke' }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="bg-gunmetal/30 border border-gunmetal rounded px-3 py-2">
      <div className="text-xs text-whitesmoke/60">{label}</div>
      <div className={clsx('text-xl font-semibold mt-0.5', color)}>{value}</div>
    </div>
  )
}
